import React, { useEffect, useState } from 'react';
import { useSpAccount, SpMarketplace } from '../SpAccountContext';
import { API_BASE } from '../api';

interface MarketplaceOption extends SpMarketplace {}

const REGION_META: Record<string, { label: string; emoji: string; colorVar: string; countries: string }> = {
  NA: { label: 'North America', emoji: '🌎', colorVar: 'var(--info)',    countries: 'US · CA · MX · BR' },
  EU: { label: 'Europe',        emoji: '🌍', colorVar: 'var(--success)', countries: 'GB · DE · FR · IT +9' },
  FE: { label: 'Far East',      emoji: '🌏', colorVar: 'var(--warning)', countries: 'JP · AU' },
};

const FLAG: Record<string, string> = {
  US:'🇺🇸', GB:'🇬🇧', DE:'🇩🇪', FR:'🇫🇷', IT:'🇮🇹', ES:'🇪🇸', CA:'🇨🇦', MX:'🇲🇽',
  NL:'🇳🇱', SE:'🇸🇪', PL:'🇵🇱', BE:'🇧🇪', TR:'🇹🇷', AE:'🇦🇪', SA:'🇸🇦', IN:'🇮🇳',
  BR:'🇧🇷', JP:'🇯🇵', AU:'🇦🇺',
};

const COUNTRY: Record<string, string> = {
  US:'United States', GB:'United Kingdom', DE:'Germany', FR:'France',
  IT:'Italy', ES:'Spain', CA:'Canada', MX:'Mexico', NL:'Netherlands',
  SE:'Sweden', PL:'Poland', BE:'Belgium', TR:'Turkey', AE:'UAE',
  SA:'Saudi Arabia', IN:'India', BR:'Brazil', JP:'Japan', AU:'Australia',
};

const emptyForm = {
  seller_id: '', account_name: '', region: 'NA' as 'NA'|'EU'|'FE',
  lwa_client_id: '', lwa_client_secret: '', lwa_refresh_token: '',
  marketplace_ids: [] as number[], is_default: false,
};

/* ── Tiny inline SVG icons ───────────────────────────────────────────── */
const Ic = {
  Amazon: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.226-.088.39-.046.525.13.12.174.09.336-.1.48-.256.19-.6.41-1.006.654a18.502 18.502 0 01-8.49 2.186c-4.38 0-8.237-1.088-11.57-3.264-.333-.22-.455-.455-.285-.655zm8.75-6.46c0-1.26.298-2.358.913-3.29.615-.937 1.432-1.637 2.5-2.098.968-.413 2.133-.65 3.47-.7l1.28-.053v-.588c0-.924-.1-1.56-.308-1.913-.207-.354-.585-.52-1.144-.52h-.065c-.45.01-.814.162-1.083.455-.27.294-.44.73-.52 1.315-.065.47-.273.724-.628.728l-3.456-.385c-.33-.08-.49-.263-.49-.55 0-.023.004-.047.01-.07.32-1.66 1.07-2.903 2.27-3.73C13.29.423 14.786.01 16.596 0h.338c2.14 0 3.785.614 4.935 1.843 1.15 1.23 1.72 2.882 1.72 4.957v9.05H20.3v-1.73c-.48.643-1.066 1.14-1.76 1.49-.695.352-1.454.528-2.29.528-1.335 0-2.43-.394-3.285-1.18-.855-.787-1.282-1.857-1.282-3.21l.113-.186zm3.58.29c0 .648.17 1.154.51 1.52.34.364.793.548 1.358.548.56 0 1.077-.186 1.55-.557.472-.372.8-.854.985-1.447.136-.43.204-.958.204-1.586v-.807l-.945.04c-1.114.048-1.94.28-2.48.695-.54.415-.81 1.025-.81 1.83l.628-.236z"/>
    </svg>
  ),
  User: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Key: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="5.5"/><path d="M21 2l-9.6 9.6"/><path d="M15.5 7.5l3 3L22 7l-3-3"/>
    </svg>
  ),
  Globe: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
    </svg>
  ),
  Eye: ({ off }: { off?: boolean }) => off ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.88 9.88a3 3 0 104.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0112 5c7 0 10 7 10 7a13.16 13.16 0 01-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 002 12s3 7 10 7a9.74 9.74 0 005.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/>
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  Check: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  X: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Edit: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  Trash: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
    </svg>
  ),
  Star: ({ filled }: { filled?: boolean }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  Wifi: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.55a11 11 0 0114.08 0"/><path d="M1.42 9a16 16 0 0121.16 0"/><path d="M8.53 16.11a6 6 0 016.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/>
    </svg>
  ),
  Plus: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
};

/* ── Password input with show/hide ──────────────────────────────────── */
const SecretInput: React.FC<{
  value: string; onChange: (v: string) => void; placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder ?? '••••••••••••'}
        style={{ paddingRight: 42 }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={{
          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 3,
          color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
          borderRadius: 4, transition: 'color 0.15s',
        }}
        tabIndex={-1}
      >
        <Ic.Eye off={show} />
      </button>
    </div>
  );
};

/* ── Section header inside drawer ────────────────────────────────────── */
const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <div className="drawer-section">
    <div className="drawer-section-header">
      <span className="drawer-section-icon">{icon}</span>
      <span className="drawer-section-title">{title}</span>
    </div>
    {children}
  </div>
);

/* ── Main component ──────────────────────────────────────────────────── */
const SpAccountList: React.FC = () => {
  const token = localStorage.getItem('access_token') ?? '';
  const { accounts, reload } = useSpAccount();
  const [allMarketplaces, setAllMarketplaces] = useState<MarketplaceOption[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editSellerId, setEditSellerId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/sp-api/marketplaces`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(setAllMarketplaces).catch(() => {});
  }, [token]);

  const filteredMarkets = allMarketplaces.filter(m => m.sp_api_region === form.region);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditSellerId(null);
    setError('');
    setTestResult(null);
    setShowForm(true);
  };

  const openEdit = async (sellerId: string) => {
    const acc = accounts.find(a => a.seller_id === sellerId);
    if (!acc) return;
    // Open immediately with what we already have from the list, then fetch the
    // full record (which includes the stored API credentials) to pre-fill them.
    setForm({
      seller_id: acc.seller_id,
      account_name: acc.account_name,
      region: acc.region as 'NA'|'EU'|'FE',
      lwa_client_id: '', lwa_client_secret: '', lwa_refresh_token: '',
      marketplace_ids: acc.marketplaces.map(m => m.id),
      is_default: acc.is_default,
    });
    setEditSellerId(sellerId);
    setError('');
    setTestResult(null);
    setShowForm(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${sellerId}`, { headers });
      if (res.ok) {
        const detail = await res.json();
        setForm(f => ({
          ...f,
          lwa_client_id: detail.lwa_client_id ?? '',
          lwa_client_secret: detail.lwa_client_secret ?? '',
          lwa_refresh_token: detail.lwa_refresh_token ?? '',
        }));
      }
    } catch { /* keep blank — user can re-enter credentials */ }
  };

  const toggleMarket = (id: number) =>
    setForm(f => ({
      ...f,
      marketplace_ids: f.marketplace_ids.includes(id)
        ? f.marketplace_ids.filter(x => x !== id)
        : [...f.marketplace_ids, id],
    }));

  const handleTest = async () => {
    if (!form.seller_id) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${form.seller_id}/test`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      setTestResult({
        ok: res.ok,
        msg: res.ok ? 'Connection successful' : (data.detail || 'Connection failed'),
      });
    } catch {
      setTestResult({ ok: false, msg: 'Network error' });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const isEdit = !!editSellerId;
      const body = isEdit
        ? {
            account_name: form.account_name,
            lwa_client_id:      form.lwa_client_id      || undefined,
            lwa_client_secret:  form.lwa_client_secret  || undefined,
            lwa_refresh_token:  form.lwa_refresh_token  || undefined,
            marketplace_ids: form.marketplace_ids,
            is_default: form.is_default,
          }
        : form;

      const res = await fetch(
        isEdit ? `${API_BASE}/api/v1/sp-api/accounts/${editSellerId}` : `${API_BASE}/api/v1/sp-api/accounts`,
        { method: isEdit ? 'PATCH' : 'POST', headers, body: JSON.stringify(body) },
      );
      if (!res.ok) {
        const d = await res.json();
        setError(d.detail || 'Save failed');
        return;
      }
      await reload();
      setShowForm(false);
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sellerId: string) => {
    if (!window.confirm('Delete this account and all its data?')) return;
    setDeleting(sellerId);
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${sellerId}`, { method: 'DELETE', headers });
      if (!res.ok) {
        let detail = `Delete failed (${res.status})`;
        try { detail = (await res.json()).detail || detail; } catch { /* no body */ }
        window.alert(detail);
        return;
      }
      await reload();
    } catch {
      window.alert('Network error — could not delete account.');
    } finally { setDeleting(null); }
  };

  const handleSetDefault = async (sellerId: string) => {
    try {
      await fetch(`${API_BASE}/api/v1/sp-api/accounts/${sellerId}/set-default`, { method: 'POST', headers });
      await reload();
    } catch { /* ignore */ }
  };

  return (
    <div>
      {/* ── Page header ───────────────────────────────────────────── */}
      <div className="page-header">
        <div className="page-header-text" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: 'linear-gradient(135deg, #ff9900 0%, #e67300 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', boxShadow: '0 4px 14px rgba(255,153,0,0.35)',
          }}>
            <Ic.Amazon />
          </div>
          <div>
            <h2>Amazon Accounts</h2>
            <p>Manage SP‑API seller accounts across all regions</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreate} style={{ gap: 7 }}>
          <Ic.Plus /> Add Account
        </button>
      </div>

      {/* ── Empty state ───────────────────────────────────────────── */}
      {accounts.length === 0 && !showForm && (
        <div className="card" style={{ textAlign: 'center', padding: '52px 32px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 18, margin: '0 auto 18px',
            background: 'linear-gradient(135deg, rgba(255,153,0,0.15), rgba(255,153,0,0.05))',
            border: '1px solid rgba(255,153,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
          }}>🛒</div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: 'var(--text-heading)' }}>
            No Amazon accounts connected
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13.5, maxWidth: 380, margin: '0 auto 28px', lineHeight: 1.6 }}>
            Connect your Amazon Seller Central credentials to start pulling orders, inventory, and business reports.
          </p>
          <button className="btn btn-primary" onClick={openCreate} style={{ padding: '10px 28px' }}>
            Connect Your First Account
          </button>
        </div>
      )}

      {/* ── Account cards ─────────────────────────────────────────── */}
      {accounts.map(acc => {
        const rm = REGION_META[acc.region] ?? REGION_META.NA;
        return (
          <div key={acc.seller_id} className={`sp-account-card region-${acc.region}`}>
            {/* Subtle region tint */}
            <div style={{
              position: 'absolute', inset: 0, left: 3, pointerEvents: 'none',
              background: `linear-gradient(90deg, ${rm.colorVar}08 0%, transparent 220px)`,
            }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>

              {/* Left — account info */}
              <div style={{ flex: 1, minWidth: 0 }}>

                {/* Name row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-heading)', letterSpacing: '-0.1px' }}>
                    {acc.account_name}
                  </span>

                  {/* Region badge */}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: `color-mix(in srgb, ${rm.colorVar} 12%, transparent)`,
                    color: rm.colorVar, border: `1px solid color-mix(in srgb, ${rm.colorVar} 25%, transparent)`,
                  }}>
                    {rm.emoji} {rm.label}
                  </span>

                  {/* Default badge */}
                  {acc.is_default && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: 'var(--accent-soft)', color: 'var(--accent)',
                      border: '1px solid var(--border-accent)',
                    }}>
                      <Ic.Star filled /> Default
                    </span>
                  )}

                  {/* Active badge */}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: acc.is_active ? 'var(--success-bg)' : 'var(--danger-bg)',
                    color: acc.is_active ? 'var(--success)' : 'var(--danger)',
                    border: `1px solid ${acc.is_active ? 'var(--success-border)' : 'var(--danger-border)'}`,
                  }}>
                    <span style={{
                      width: 5, height: 5, borderRadius: '50%', display: 'inline-block',
                      background: 'currentColor',
                    }} />
                    {acc.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Seller ID */}
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 2 }}>
                  Seller ID&nbsp;
                  <code style={{ fontSize: 11.5, letterSpacing: '0.05em' }}>{acc.seller_id}</code>
                </div>

                {acc.last_verified_at && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 11.5, marginBottom: 0 }}>
                    Verified {new Date(acc.last_verified_at).toLocaleString()}
                  </div>
                )}

                {/* Marketplace pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 12 }}>
                  {acc.marketplaces.map(m => (
                    <span key={m.id} title={COUNTRY[m.code] ?? m.code} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 10px', borderRadius: 7, fontSize: 12, fontWeight: 500,
                      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
                      color: 'var(--text-secondary)', cursor: 'default',
                    }}>
                      <span style={{ fontSize: 14 }}>{FLAG[m.code] ?? '🌐'}</span>
                      {m.code}
                    </span>
                  ))}
                  {acc.marketplaces.length === 0 && (
                    <span style={{ fontSize: 12, color: 'var(--warning)' }}>⚠ No marketplaces selected</span>
                  )}
                </div>
              </div>

              {/* Right — actions */}
              <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
                {!acc.is_default && (
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => handleSetDefault(acc.seller_id)}
                    title="Set as default account"
                    style={{ gap: 5 }}
                  >
                    <Ic.Star /> Default
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => openEdit(acc.seller_id)}
                  style={{ gap: 5 }}
                >
                  <Ic.Edit /> Edit
                </button>
                <button
                  className="btn btn-small"
                  onClick={() => handleDelete(acc.seller_id)}
                  disabled={deleting === acc.seller_id}
                  style={{
                    gap: 5, background: 'var(--danger-bg)',
                    border: '1px solid var(--danger-border)', color: 'var(--danger)',
                    opacity: deleting === acc.seller_id ? 0.6 : 1,
                  }}
                >
                  <Ic.Trash /> {deleting === acc.seller_id ? '…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Drawer ────────────────────────────────────────────────── */}
      {showForm && (
        <div className="drawer-overlay" onClick={() => setShowForm(false)}>
          <div className="drawer" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="drawer-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                  background: 'linear-gradient(135deg, #ff9900 0%, #e67300 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                }}>
                  <Ic.Amazon />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14.5, color: 'var(--text-heading)', lineHeight: 1.2 }}>
                    {editSellerId ? 'Edit Account' : 'Add Amazon Account'}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                    {editSellerId
                      ? `Seller ID: ${editSellerId}`
                      : 'Connect a new Seller Central account'}
                  </div>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setShowForm(false)}>
                <Ic.X />
              </button>
            </div>

            {/* Body */}
            <div className="drawer-body">

              {/* Error */}
              {error && (
                <div className="alert alert-error" style={{ marginBottom: 20 }}>
                  <Ic.X /> {error}
                </div>
              )}

              {/* Section 1 — Account Info */}
              <Section icon={<Ic.User />} title="Account Info">
                <div className="drawer-field">
                  <label className="form-label">Seller ID *</label>
                  <input
                    className="form-input"
                    value={form.seller_id}
                    disabled={!!editSellerId}
                    onChange={e => setForm(f => ({ ...f, seller_id: e.target.value }))}
                    placeholder="e.g. A1EXAMPLE123456"
                  />
                </div>
                <div className="drawer-field">
                  <label className="form-label">Account Name *</label>
                  <input
                    className="form-input"
                    value={form.account_name}
                    onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))}
                    placeholder="e.g. EU Main Account"
                  />
                </div>

                {/* Region — only for create */}
                {!editSellerId && (
                  <div className="drawer-field">
                    <label className="form-label">Region *</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 2 }}>
                      {(Object.entries(REGION_META) as [string, typeof REGION_META[string]][]).map(([code, rm]) => {
                        const sel = form.region === code;
                        return (
                          <button
                            key={code}
                            type="button"
                            className={`region-card${sel ? ` selected-${code}` : ''}`}
                            onClick={() => setForm(f => ({ ...f, region: code as 'NA'|'EU'|'FE', marketplace_ids: [] }))}
                          >
                            <div style={{ fontSize: 24, marginBottom: 5 }}>{rm.emoji}</div>
                            <div style={{
                              fontWeight: 700, fontSize: 12, lineHeight: 1.2,
                              color: sel ? rm.colorVar : 'var(--text-primary)',
                            }}>{rm.label}</div>
                            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
                              {rm.countries}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Section>

              {/* Section 2 — API Credentials */}
              <Section icon={<Ic.Key />} title="API Credentials">
                {editSellerId && (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
                    Leave fields blank to keep existing credentials.
                  </p>
                )}
                <div className="drawer-field">
                  <label className="form-label">LWA Client ID *</label>
                  <input
                    className="form-input"
                    value={form.lwa_client_id}
                    onChange={e => setForm(f => ({ ...f, lwa_client_id: e.target.value }))}
                    placeholder="amzn1.application-oa2-client.xxx"
                  />
                </div>
                <div className="drawer-field">
                  <label className="form-label">LWA Client Secret *</label>
                  <SecretInput
                    value={form.lwa_client_secret}
                    onChange={v => setForm(f => ({ ...f, lwa_client_secret: v }))}
                  />
                </div>
                <div className="drawer-field">
                  <label className="form-label">LWA Refresh Token *</label>
                  <SecretInput
                    value={form.lwa_refresh_token}
                    onChange={v => setForm(f => ({ ...f, lwa_refresh_token: v }))}
                  />
                </div>
              </Section>

              {/* Section 3 — Marketplaces */}
              <Section icon={<Ic.Globe />} title="Marketplaces">
                {filteredMarkets.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    No marketplaces available for this region.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {filteredMarkets.map(m => {
                      const active = form.marketplace_ids.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          className={`market-pill${active ? ' active' : ''}`}
                          onClick={() => toggleMarket(m.id)}
                          title={COUNTRY[m.code] ?? m.code}
                        >
                          <span style={{ fontSize: 15 }}>{FLAG[m.code] ?? '🌐'}</span>
                          <span>{COUNTRY[m.code] ?? m.code}</span>
                          {active && <Ic.Check />}
                        </button>
                      );
                    })}
                  </div>
                )}

                <label style={{
                  display: 'flex', alignItems: 'center', gap: 9, marginTop: 16,
                  cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', userSelect: 'none',
                }}>
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                    style={{ width: 15, height: 15, accentColor: 'var(--accent)', cursor: 'pointer' }}
                  />
                  Set as default account
                </label>
              </Section>

              {/* Test connection result */}
              {testResult && (
                <div className={`alert ${testResult.ok ? 'alert-success' : 'alert-error'}`}>
                  {testResult.ok ? <Ic.Check /> : <Ic.X />}
                  {testResult.msg}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="drawer-footer">
              {editSellerId && (
                <button
                  className="btn btn-secondary"
                  onClick={handleTest}
                  disabled={testing}
                  style={{ flex: 1, gap: 6 }}
                >
                  <Ic.Wifi />
                  {testing ? 'Testing…' : 'Test Connection'}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
                style={{ flex: 2 }}
              >
                {saving ? 'Saving…' : editSellerId ? 'Save Changes' : 'Add Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpAccountList;
